import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { translations } from '../i18n/translations';
import { detectLanguageFromPath, HTML_LANG } from '../config/routeMap';

const LanguageContext = createContext();

/**
 * LanguageProvider (Phase C refactor)
 *
 * The active language is now DERIVED FROM THE URL PATHNAME, not from
 * localStorage. This is the source of truth for SEO. localStorage is only
 * used to remember the user's LAST CHOSEN language for the initial landing
 * on `/` (x-default page), where the RootLanguageChooser can offer a smart
 * default (not used to auto-redirect — Decision 2 forbids that).
 *
 * On routes with no language prefix (`/`), `language` defaults to 'en'
 * (fallback for i18n lookups, but the x-default page shows both languages).
 */
export const LanguageProvider = ({ children }) => {
  const location = useLocation();

  const language = useMemo(() => {
    const detected = detectLanguageFromPath(location.pathname);
    if (detected) return detected;
    // Fallback for x-default root `/` — use stored preference or browser lang
    try {
      const stored = typeof window !== 'undefined' && localStorage.getItem('myguardpod-lang');
      if (stored === 'fr' || stored === 'en') return stored;
    } catch (e) { /* ignore */ }
    if (typeof navigator !== 'undefined') {
      const browserLang = (navigator.language || '').toLowerCase();
      if (browserLang.startsWith('fr')) return 'fr';
    }
    return 'en';
  }, [location.pathname]);

  // Persist last-known language for a smarter default next time the user
  // lands on `/` (does NOT trigger auto-redirect).
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.setItem('myguardpod-lang', language);
      }
    } catch (e) { /* ignore */ }
    // Keep <html lang> synced with the URL-derived language
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', HTML_LANG[language] || 'en');
    }
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language] || translations.en;
    for (const k of keys) {
      if (value === undefined || value === null) return key;
      value = value[k];
    }
    return value !== undefined && value !== null ? value : key;
  };

  // Legacy no-op — kept so existing components importing setLanguage/toggle
  // don't crash. Language changes are now driven by navigation.
  const setLanguage = () => { /* no-op; use LanguageSwitcher */ };
  const toggleLanguage = () => { /* no-op; use LanguageSwitcher */ };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
